import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

const ADMIN_ID = '00000000-0000-0000-0000-000000000000';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      const adminSession = localStorage.getItem('jinxos_admin_session');
      if (adminSession) {
        const parsed = JSON.parse(adminSession);
        const { data: profile } = await supabase.from('profiles_1740000000000').select('*').eq('id', ADMIN_ID).maybeSingle();
        setUser({ ...parsed, ...(profile || {}) });
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await fetchProfile(session.user);
      } else {
        setLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        fetchProfile(session.user);
      } else {
        if (!localStorage.getItem('jinxos_admin_session')) {
          setUser(null);
        }
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (authUser) => {
    try {
      const { data: profile } = await supabase
        .from('profiles_1740000000000')
        .select('*')
        .eq('id', authUser.id)
        .single();

      const isSuperAdmin = authUser.email === 'info@cimmeronstudios.com';
      setUser({
        ...authUser,
        ...(profile || {}),
        role: isSuperAdmin ? 'SUPER ADMIN' : (profile?.role || 'User')
      });
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    if (email === 'info@cimmeronstudios.com' && password === 'crm123') {
      const adminUser = {
        id: ADMIN_ID,
        email: 'info@cimmeronstudios.com',
        name: 'Super Admin',
        role: 'SUPER ADMIN',
        company: 'Cimmeron Studios'
      };
      await supabase.from('profiles_1740000000000').upsert({
        id: ADMIN_ID,
        name: 'Super Admin',
        email: 'info@cimmeronstudios.com',
        role: 'SUPER ADMIN',
        company: 'Cimmeron Studios'
      });
      setUser(adminUser);
      localStorage.setItem('jinxos_admin_session', JSON.stringify(adminUser));
      return { success: true };
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { success: false, error: error.message };
    localStorage.removeItem('jinxos_admin_session');
    return { success: true };
  };

  const register = async (userData) => {
    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
    });

    if (error) return { success: false, error: error.message };
    
    if (data.user) {
      await Promise.all([
        supabase.from('profiles_1740000000000').insert({
          id: data.user.id,
          name: userData.name,
          email: userData.email,
          company: userData.company,
          role: userData.role || 'User'
        }),
        supabase.from('business_settings_1740000000000').insert({
          user_id: data.user.id,
          company_name: userData.company,
          email: userData.email
        })
      ]);
    }
    return { success: true };
  };

  const adminCreateUser = async (userData) => {
    // This uses the standard register logic but returns the admin to their state
    // In a real app, this would use a Supabase Edge Function to avoid session swapping
    const result = await register(userData);
    
    // If the admin was hardcoded, restore their session in local storage/state
    const adminSession = localStorage.getItem('jinxos_admin_session');
    if (adminSession) {
      const parsed = JSON.parse(adminSession);
      setUser(parsed);
    }
    
    return result;
  };

  const logout = async () => {
    localStorage.removeItem('jinxos_admin_session');
    await supabase.auth.signOut();
    setUser(null);
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    const dbPayload = {
      name: updates.name,
      avatar: updates.avatar,
      phone: updates.phone,
      company: updates.company,
      role: updates.role
    };
    const { error } = await supabase.from('profiles_1740000000000').update(dbPayload).eq('id', user.id);
    if (!error) {
      setUser(prev => ({ ...prev, ...updates }));
      if (user.id === ADMIN_ID) {
        localStorage.setItem('jinxos_admin_session', JSON.stringify({ ...user, ...updates }));
      }
    }
    return { error };
  };

  return (
    <AuthContext.Provider value={{ user, login, register, adminCreateUser, logout, updateProfile, loading }}>
      {children}
    </AuthContext.Provider>
  );
};